(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define(['underscore', 'torso/modules/ServiceCell', 'app/storage/sessionStorageService'], factory);
  } else if (typeof exports === 'object') {
    // CommonJS-style exports
    var _ = require('underscore');
    var TorsoServiceCell = require('backbone-torso/modules/ServiceCell');
    var sessionStorageService = require('./../storage/sessionStorageService');
    module.exports = factory(_, TorsoServiceCell, sessionStorageService);
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function(_, TorsoServiceCell, sessionStorageService) {
  'use strict';
  var workflowConfigKey = sessionStorageService.SESSION_STORAGE_KEYS.eregWorkflowConfig;

  // Map of ereg to wizard step names if they differ
  var EREG_TO_WIZARD_STEP_NAME_MAP = {
    'demographics' : 'profile',
    'insurance': 'insurance',
    'questionnaire' : 'surveys',
    'form' : 'forms',
    'financial' : 'payment'
  };

  /**
   * This service manages interactions with the ereg workflow configuration.  Has basic set/get/remove methods for the
   * ereg config from the session storage along with some helper methods to get disabled wizard steps specifically and
   * check if a step is enabled or not.
   *
   * @class eregWorkflowConfigService
   * @static
   */
  return new (TorsoServiceCell.extend({
    /**
     * @method initialize
     * @override
     */
    initialize: function() {
      _.bindAll(this, 'getDisabledWizardSteps');
    },

    /**
     * Retrieves ereg workflow config from session storage
     *
     * @method getEregWorkflowConfig
     * @return {Object} ereg workflow config
     */
    getEregWorkflowConfig: function() {
      return sessionStorageService.getItem(workflowConfigKey) || {};
    },

    /**
     * Sets ereg workflow config to session storage.
     * Update the workflow based on the translation from the UI selection name to the wizard step name.
     *
     * @method setEregWorkflowConfig
     * @param eregWorkflowConfig {Object} ereg workflow config
     *   @param eregWorkflowConfig.disabledSteps {Object} map from step name as defined by the selection UI to whether it is enabled.
     *   @param eregWorkflowConfig.wizardDisabledSteps {Object} map from step name as defined by the wizard to whether it is enabled.
     */
    setEregWorkflowConfig: function(eregWorkflowConfig) {
      var eregDisabledNodes = eregWorkflowConfig.disabledSteps || {};
      eregWorkflowConfig.wizardDisabledSteps = eregWorkflowConfig.wizardDisabledSteps || {};
      _.each(eregDisabledNodes, function(value, key) {
        if (value) {
          var stepName = EREG_TO_WIZARD_STEP_NAME_MAP[key] || key;
          eregWorkflowConfig.wizardDisabledSteps[stepName] = true;
        }
      });
      sessionStorageService.setItem(workflowConfigKey, eregWorkflowConfig);
    },

    /**
     * Removes the workflow config from the session storage.
     *
     * @method removeEregWorkflowConfig
     */
    removeEregWorkflowConfig: function() {
      sessionStorageService.removeItem(workflowConfigKey);
    },

    /**
     * Retrieves disabled top-level wizard steps from session storage, qualified by Patient & Appointment
     *
     * @method getDisabledWizardSteps
     * @param options {Object} Options to check data against
     *   @param options.patientVecnaId {String} the VecnaID of the Patient being displayed
     *   @param options.appointmentExternalId {String} the ExternalID of the Appointment being displayed
     *   @param options.regDate {String} the date of the Appointments being displayed
     * @return {Object} keys correspond to disabled steps
     */
    getDisabledWizardSteps: function(options) {
      var eregWorkflowConfig = this.getEregWorkflowConfig();

      var isWorkflowForDisplayedEntity = this._isWorkflowForPatientAppointment({
        workflowConfig: eregWorkflowConfig,
        patientVecnaId: options.patientVecnaId,
        appointmentExternalId: options.appointmentExternalId,
        regDate: options.regDate
      });

      var wizardDisabledSteps;
      if (isWorkflowForDisplayedEntity && eregWorkflowConfig && eregWorkflowConfig.wizardDisabledSteps) {
        wizardDisabledSteps = eregWorkflowConfig.wizardDisabledSteps;
      }
      return wizardDisabledSteps || {};
    },

    /**
     * @method isEnabledStep
     * @param options {Object} Options to check step data against
     *   @param options.topLevelStepName {String} name of step to find
     *   @param options.patientVecnaId {String} the VecnaID of the Patient being displayed
     *   @param options.appointmentExternalId {String} the ExternalID of the Appointment being displayed
     *   @param options.regDate {String} the registration date the Appointments being displayed
     * @return {Boolean} true if step enabled, false if disabled
     */
    isEnabledStep: function(options) {
      var disabledSteps = this.getDisabledWizardSteps({
        patientVecnaId: options.patientVecnaId,
        appointmentExternalId: options.appointmentExternalId,
        regDate: options.regDate
      });
      return !disabledSteps[options.topLevelStepName];
    },

    /**
     * Answers if a WorkflowConfiguration object belongs to a specific Patient & Appointment(s) combination
     * @param options {Object}
     *   @param options.workflowConfig {Object} ereg workflow config
     *   @param options.patientVecnaId {String} the VecnaId of the Patient an ereg view is for.
     *   @param options.appointmentExternalId {String} the ExternalId of the Appointment an ereg view is for.
     *   @param options.regDate {String} the date of the Appointment an ereg view is for.
     * @return {Boolean} true if the Configuration's Patient and Appointment(s) match the passed arguments
     * @private
     */
    _isWorkflowForPatientAppointment: function(options) {
      var workflowConfig = options && options.workflowConfig;
      if (!workflowConfig) {
        return false;
      }

      var isMatchingPatient = options.patientVecnaId && (workflowConfig.patientVecnaId === options.patientVecnaId);
      var isMatchingAppointmentDate = options.regDate && (workflowConfig.regDate === options.regDate);
      var isMatchingAppointment = options.appointmentExternalId && (workflowConfig.appointmentExtId === options.appointmentExternalId);
      return isMatchingPatient && (isMatchingAppointment || isMatchingAppointmentDate);
    }
  }))();
}));

